import { ReactElement, useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { Internship } from '@backrs_ui/common-ui/src/models/internship';
import {
  ASK_LUBAV_BUTTON_ENDPOINTS,
  AskLubavButtonEndpoint,
} from '@backrs_ui/common-ui/src/models/nugget';
import {
  AskLubavButtonGroup,
  AskLubavButtonGroupProps,
  ButtonConfig,
  MappedButtonConfigs,
} from '@backrs_ui/common-ui/src/molecules/AskLubavButtons/AskLubavButtonGroup';
import { ApiKeyAction } from '@backrs_ui/common-ui/src/molecules/AskLubavButtons/AskLubavResponse';

import { Box, SxProps, Theme } from '@mui/material';

import { BackrsAssessment } from './BackrsAssessment';
import { QuickLook } from './QuickLook';

const INTERNSHIP_STRINGS = appStrings.modules.nugget.internship;

type NuggetInternshipLargeProps = Readonly<{
  internshipInfo: Internship;
  askLubavButtonGroupProps?: Omit<AskLubavButtonGroupProps, 'buttonConfigs'>;
  apiKeyOnClick?: ApiKeyAction[];
  askLubavButtonResponse: string | undefined;
}>;

export function NuggetInternshipLarge({
  internshipInfo,
  askLubavButtonGroupProps,
  apiKeyOnClick,
  askLubavButtonResponse,
}: NuggetInternshipLargeProps): ReactElement {
  const [isLogoLinkValid, setIsLogoLinkValid] = useState(true);

  const buttonConfigs: MappedButtonConfigs = new Map<AskLubavButtonEndpoint, ButtonConfig>([
    [
      ASK_LUBAV_BUTTON_ENDPOINTS.INTERNSHIP_AM_I_A_GOOD_FIT,
      {
        buttonTitle: INTERNSHIP_STRINGS.askLubav.amIAGoodFit,
        disabledInProd: true,
        requirements: {
          requiresResume: true,
        },
        onClick: apiKeyOnClick?.find(
          (x) => x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.INTERNSHIP_AM_I_A_GOOD_FIT,
        )?.onClick,
        actionButtonText: apiKeyOnClick?.find(
          (x) => x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.INTERNSHIP_AM_I_A_GOOD_FIT,
        )?.actionButtonText,
      },
    ],

    [
      ASK_LUBAV_BUTTON_ENDPOINTS.INTERNSHIP_SIMILAR_ROLES,
      {
        buttonTitle: INTERNSHIP_STRINGS.askLubav.similarRoles,
        disabledInProd: true,
        onClick: apiKeyOnClick?.find(
          (x) => x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.INTERNSHIP_SIMILAR_ROLES,
        )?.onClick,
        actionButtonText: apiKeyOnClick?.find(
          (x) => x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.INTERNSHIP_SIMILAR_ROLES,
        )?.actionButtonText,
      },
    ],
    [
      ASK_LUBAV_BUTTON_ENDPOINTS.INTERNSHIP_INCREASE_MY_ODDS,
      {
        buttonTitle: INTERNSHIP_STRINGS.askLubav.increaseMyOdds,
        disabledInProd: true,
        requirements: {
          requiresResume: true,
        },
        onClick: apiKeyOnClick?.find(
          (x) => x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.INTERNSHIP_INCREASE_MY_ODDS,
        )?.onClick,
        actionButtonText: apiKeyOnClick?.find(
          (x) => x?.key === ASK_LUBAV_BUTTON_ENDPOINTS.INTERNSHIP_INCREASE_MY_ODDS,
        )?.actionButtonText,
      },
    ],
  ]);

  return (
    <Box sx={largeInternshipContainerStyle}>
      {askLubavButtonGroupProps && (
        <AskLubavButtonGroup
          buttonConfigs={buttonConfigs}
          {...askLubavButtonGroupProps}
          askLubavButtonResponse={askLubavButtonResponse}
        />
      )}

      {internshipInfo.ai_description}

      <Box sx={textContainerStyle}>
        <QuickLook internshipInfo={internshipInfo} />
        <BackrsAssessment />
      </Box>

      {internshipInfo.logo_link && isLogoLinkValid && (
        <Box
          component="img"
          src={internshipInfo.logo_link}
          alt="Internship"
          sx={logoImageStyle}
          onError={() => setIsLogoLinkValid(false)}
        />
      )}
    </Box>
  );
}

const largeInternshipContainerStyle: SxProps<Theme> = {
  mt: 3,
  gap: 2,
  display: 'flex',
  flexDirection: 'column',
};

const textContainerStyle: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 11fr)',
  mt: 3,
  gap: 2,
};

const logoImageStyle: SxProps<Theme> = {
  maxWidth: '100px',
  minWidth: '100px',
  maxHeight: '100px',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
  alignSelf: 'flex-end',
  mt: 0,
  ml: 4,
};
